import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { Stats } from '@flakiness/server/common/stats/stats.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { consume, ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { Temporal } from 'temporal-polyfill';
import { api } from '../api.js';
import { contexts } from '../contexts.js';
import { AppLink, handleLink, hrefOrClick, Linkifier } from '../linkifier.js';
import { linkStyles } from './cssstyles.js';
import { DayData, DayStatus } from './github-calendar.js';

export type TestHealthCalendarState = {
  selectedSince: Temporal.PlainDate,
  selectedUntil: Temporal.PlainDate,
  viewportSince: Temporal.PlainDate,
  viewportUntil: Temporal.PlainDate,
}

export type TestHealthCalendarLinkifier = Linkifier<TestHealthCalendarState>;

// How far the left and right arrows scroll the calendar.
const SHIFT_WEEKS = 2;

function weekStart(date: Temporal.PlainDate) {
  return date.subtract({ days: date.dayOfWeek % 7 });
}

export function createTestHealthCalendarState(state: Partial<TestHealthCalendarState> = {}) {
  let { selectedUntil, selectedSince } = state;
  // No selection? Just bail out right away. Otherwise we risk starting loading some completely
  // irrelevant data.
  if (!selectedSince && !selectedUntil)
    return undefined;

  if (!selectedSince || !selectedUntil)
    selectedUntil = selectedSince = selectedSince ?? selectedUntil ?? Temporal.Now.plainDateISO();
  
  if (Temporal.PlainDate.compare(selectedSince, selectedUntil) > 0)
    [selectedSince, selectedUntil] = [selectedUntil, selectedSince];

  let viewportSince = state?.viewportSince ?? weekStart(selectedUntil).subtract({ weeks: 11 });
  let viewportUntil = state?.viewportUntil ?? viewportSince.add({ weeks: 12 }).subtract({ days: 1 });
  if (Temporal.PlainDate.compare(viewportSince, viewportUntil) > 0)
    [viewportSince, viewportUntil] = [viewportUntil, viewportSince];

  // Defend against faulty viewport values.
  if (weeksToShift(viewportSince, viewportUntil) >= 12)
    viewportUntil = viewportSince.add({ weeks: 12 }).subtract({ days: 1 });

  return {
    selectedSince,
    selectedUntil,
    viewportSince,
    viewportUntil,
  }
}

function weeksToShift(since: Temporal.PlainDate, until: Temporal.PlainDate) {
  const weeks = since.until(until, { largestUnit: 'days' }).days / 7;
  return weeks < 0 ? 0 : Math.ceil(weeks);
}

export function scrollCalendarViewportToSelection(state: TestHealthCalendarState): TestHealthCalendarState {
  let { viewportSince, viewportUntil, selectedSince, selectedUntil } = state;

  const weekToStart = weeksToShift(viewportUntil, selectedSince) - weeksToShift(selectedSince, viewportSince);
  viewportSince = viewportSince.add({ weeks: weekToStart });
  viewportUntil = viewportUntil.add({ weeks: weekToStart });

  const weekToEnd = weeksToShift(viewportUntil, selectedUntil) - weeksToShift(selectedUntil, viewportSince);
  viewportSince = viewportSince.add({ weeks: weekToEnd });
  viewportUntil = viewportUntil.add({ weeks: weekToEnd });

  return { selectedSince, selectedUntil, viewportSince, viewportUntil };
}

function isCalendarSelectionOutsideViewport(state: TestHealthCalendarState): boolean {
  return Temporal.PlainDate.compare(state.selectedSince, state.viewportSince) < 0
    || Temporal.PlainDate.compare(state.selectedUntil, state.viewportUntil) > 0;
}

@customElement('test-health-calendar')
export class TestHealthCalendar extends LitElement {

  @property({ attribute: false }) head?: WireTypes.Ref;
  @property({ attribute: false }) split?: EnvGroup;
  @property({ attribute: false }) testId?: Stats.TestId;
  @property({ attribute: false }) hideRangeButtons?: boolean;
  @property({ attribute: false }) linkifier?: TestHealthCalendarLinkifier;
  @property({ attribute: false }) state?: TestHealthCalendarState;

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;

  private _dayData(): DayData[] {
    if (!this.state)
      return [];

    const cachedDays = this._cachedDaysTask.value ?? new Map();

    const out: DayData[] = [];
    for (let day = this.state.viewportSince; Temporal.PlainDate.compare(day, this.state.viewportUntil) <= 0; day = day.add({ days: 1 })) {
      out.push({
        date: day,
        status: cachedDays.get(day.toString()) ?? 'loading',
      })
    }
    return out;
  }

  // This is a smart cache: it caches results per testId x timeline x commitId. 
  private _cachedDaysTask = new Task(this, {
    args: () => [
      // everytime timeline / head changes / testId changes, we have to reset cache.
      this.split?.etag(), this.head?.commit.commitId, this.testId, this._project, this._snapshotId,
    ] as const,
    task: async ([split, head, testId, project, snapshotId], { signal }) => {
      CHECK(split && head && project && snapshotId);
      return new Map<string, DayStatus>();
    }
  });

  private _loadDaysTask = new Task(this, {
    argsEqual(allOldArgs, allNewArgs) {
      const [oldState, ...oldArgs] = allOldArgs;
      const [newState, ...newArgs] = allNewArgs;
      return oldArgs.every((arg, idx) => newArgs[idx] === arg) && JSON.stringify(oldState) === JSON.stringify(newState);
    },

    args: () => [
      this.state, this._cachedDaysTask.value, this._project, this._snapshotId, this.head?.commit.commitId, this.split, this.testId,
    ] as const,
    task: async ([state, cachedDays, project, snapshotId, head, split, testId], { signal }) => {
      CHECK(cachedDays && project && snapshotId && head && split && state);

      // If all data has been loaded, then no need to issue any loading requests.
      const data = this._dayData();
      if (!data.some(d => d.status === 'loading'))
        return;

      const dailyOutcomes = await api.report.dailyOutcomes.POST({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        head,
        timeZoneId: project.timezone,
        acceptableFlakinessRatio: project.acceptableFlakinessRatio,
        since: state.viewportSince.toString(),
        until: state.viewportUntil.toString(),
        testId,
        timelineSplit: split.serialize(),
      }, { signal });
      for (const { day, outcome } of dailyOutcomes)
        cachedDays.set(day, outcome);
      this.requestUpdate();
    }
  });

  private _lastTestedCommitTask = new Task(this, {
    argsEqual: ([oldProject, oldSnapshotId, oldHead, oldSplit, oldTestId], [newProject, newSnapshotId, newHead, newSplit, newTestId]) => {
      return oldProject === newProject
        && oldSnapshotId === newSnapshotId
        && oldHead === newHead
        && oldTestId === newTestId
        && JSON.stringify(oldSplit) === JSON.stringify(newSplit);
    },
    args: () => [
      this._project,
      this._snapshotId,
      this.head?.commit.commitId,
      this.split?.serialize(),
      this.testId,
    ] as const,
    task: async ([project, snapshotId, head, split, testId], { signal }) => {
      if (!project || !snapshotId || !head || !split)
        return undefined;
      return await api.history.lastTestedCommit.POST({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        head,
        split,
        testId,
      }, { signal });
    }
  });

  private _selectDayLink(selected: Temporal.PlainDate): AppLink {
    if (!this.state)
      return;
    const delta = this.state.selectedUntil.until(selected, { largestUnit: 'days' }).days;
    return this.linkifier?.(scrollCalendarViewportToSelection({
      ...this.state,
      selectedSince: this.state.selectedSince.add({ days: delta }),
      selectedUntil: this.state.selectedUntil.add({ days: delta }),
    }));
  }

  private _crosshairButton() {
    const lastTestedCommitDay = this._project?.timezone && this._lastTestedCommitTask.value ? Temporal.Instant
          .fromEpochMilliseconds(this._lastTestedCommitTask.value.timestamp)
          .toZonedDateTimeISO(this._project.timezone)
          .toPlainDate()
          : undefined;
    const data = this.state && isCalendarSelectionOutsideViewport(this.state) ? {
      link: this.linkifier?.(scrollCalendarViewportToSelection(this.state)),
      icon: 'arrow-counterclockwise',
      tooltip: 'Scroll back to selection',
    } : {
      link: lastTestedCommitDay ? this._selectDayLink(lastTestedCommitDay) : undefined,
      icon: 'crosshair',
      tooltip: 'Select last tested day',
    };
    return html`
      <sl-icon-button title=${data.tooltip} name=${data.icon} ${hrefOrClick(data.link)}></sl-icon-button>
    `
  }

  render() {
    return html`
      <github-calendar
        .selectedSince=${this.state?.selectedSince}
        .selectedUntil=${this.state?.selectedUntil}
        .data=${this._dayData()}
        .today=${this._project?.timezone ? Temporal.Now.plainDateISO(this._project.timezone) : undefined}
        .linkifier=${(date: Temporal.PlainDate) => this._selectDayLink(date)}
        @fk-shift-left=${() => {
          if (!this.state)
            return;
          handleLink(this._router, this.linkifier?.({
            ...this.state,
            viewportSince: this.state.viewportSince.subtract({ weeks: SHIFT_WEEKS }),
            viewportUntil: this.state.viewportUntil.subtract({ weeks: SHIFT_WEEKS }),
          }));
        }}
        @fk-shift-right=${() => {
          if (!this.state)
            return;
          handleLink(this._router, this.linkifier?.({
            ...this.state,
            viewportSince: this.state.viewportSince.add({ weeks: SHIFT_WEEKS }),
            viewportUntil: this.state.viewportUntil.add({ weeks: SHIFT_WEEKS }),
          }));
        }}
      >
        <v-box slot=widgets class=widgets>

          <head-calendar
            .maxRangeDays=${this.hideRangeButtons ? 1 : 14}
            .headRef=${this.head}
            .since=${this.state?.selectedSince}
            .until=${this.state?.selectedUntil}
            @fk-select=${(event: CustomEvent<{ since: Temporal.PlainDate, until: Temporal.PlainDate }>) => {
              if (!this._project?.timezone || !this.state)
                return;
              handleLink(this._router, this.linkifier?.(scrollCalendarViewportToSelection({
                ...this.state,
                selectedSince: event.detail.since,
                selectedUntil: event.detail.until,
              })));
            }}
          ></head-calendar>
          ${this._crosshairButton()}
          ${this.hideRangeButtons ? nothing : html`
            ${this._renderRangeSelectionButton(14, '2w')}
            ${this._renderRangeSelectionButton(7, '1w')}
            ${this._renderRangeSelectionButton(1, '1d')}
          `}

          <x-filler></x-filler>
          ${this._renderHelp()}
        </v-box>
      </github-calendar>
    `;
  }

  // With a test, the calendar shows that test's worst result of each day;
  // without one, the status of the whole suite by the end of each day.
  private _renderHelp() {
    const forTest = this.testId !== undefined;
    // Worst status first, the order in which a day picks its color.
    const legend: [WireTypes.SpanOutcome, string][] = forTest ? [
      ['regressed', 'The test has regressed'],
      ['unexpected', 'The test has failed'],
      ['flaked', 'The test has flaked'],
      ['expected', 'The test has passed'],
      ['skipped', 'The test was skipped'],
      ['untested', 'The test didn\'t run'],
      ['idle', 'There were no commits'],
    ] : [
      ['regressed', 'A test has regressed'],
      ['unexpected', 'A test has failed'],
      ['flaked', 'Flaky tests exceed the acceptable ratio'],
      ['expected', 'All tests have passed'],
      ['skipped', 'All tests were skipped'],
      ['untested', 'The commits weren\'t tested'],
      ['idle', 'There were no commits'],
    ];
    return html`
      <!-- The card opens below, so it doesn't cover the days it explains. -->
      <fk-help placement=bottom-end>
        <p class=help-title>${forTest ? 'Test health calendar' : 'Branch health calendar'}</p>
        <p>
          Each day stands for the commits made that day on the selected branch.
          ${forTest ? html`
            Its color shows the test's <em>worst</em> result across those commits,
            in the selected environment:
          ` : html`
            Its color shows the status of the test suite <em>by the end of the day</em>,
            across the selected environments:
          `}
        </p>
        <div class=legend>
          ${legend.map(([outcome, description]) => html`
            <day-outcome .outcome=${outcome}></day-outcome>
            <span>${description}</span>
          `)}
        </div>
        <p class=help-note>
          ${forTest
            ? 'A test that failed in the morning and was fixed by evening still turns the day red.'
            : 'A test that failed in the morning and was fixed by evening doesn\'t turn the day red.'}
          A late run of an earlier commit counts toward that commit's day.
        </p>
      </fk-help>
    `;
  }

  private _renderRangeSelectionButton(days: number, name: string) {
    const since = this.state?.selectedSince;
    const until = this.state?.selectedUntil;
    const tz = this._project?.timezone;
    const delta = since && until ? since.until(until, { largestUnit: 'days' }).days : undefined;
    const href = since && until && tz && this.state ? this.linkifier?.(scrollCalendarViewportToSelection({
      ...this.state,
      selectedSince: until.subtract({ days: days - 1 }),
      selectedUntil: until,
    })) : undefined;
    return html`
      <a
        ${hrefOrClick(href)}
        class=${classMap({
          'range-selection': true,
          selected: delta === days - 1,
        })}>${name}</a>
    `;
  }
  
  static styles = [linkStyles, css`
    :host {
      display: flex;
      flex-direction: column;
    }

    /* The side column puts each widget in line with a row of days. */
    .widgets {
      height: 100%;
      margin-left: var(--sl-spacing-x-small);
      align-items: center;
      gap: 0;

      & > * {
        /* The height of a row of days in <github-calendar>. */
        height: 19px;
        display: flex;
        align-items: center;
      }

      & > sl-icon-button::part(base) {
        padding: 0;
      }
    }

    .help-title {
      font-weight: var(--sl-font-weight-semibold);
      color: var(--sl-color-neutral-900);
    }

    /* One status per line: the swatch, then what it means. */
    .legend {
      display: grid;
      grid-template-columns: auto 1fr;
      align-items: center;
      gap: var(--sl-spacing-2x-small) var(--sl-spacing-x-small);
    }

    .help-note {
      color: var(--sl-color-neutral-600);
    }

    .range-selection {
      text-decoration: none;
      padding: var(--sl-spacing-3x-small) var(--sl-spacing-2x-small);
      color: var(--sl-color-neutral-600);
      font-family: monospace;

      &:hover {
        color: var(--sl-color-primary-600);
        cursor: pointer;
      }

      &.selected {
        background-color: var(--sl-color-neutral-100);
        border-radius: var(--sl-border-radius-medium);
        outline: 1px solid var(--fk-color-border);
      }
    }
  `];
}
